import React from 'react';
import { Play, Instagram, Film } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent } from './ui/card';

const Videos = ({ videoCategories, instagramUrl }) => {
  return (
    <section id="videos" className="py-20 bg-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-burgundy/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-pink/10 rounded-full blur-3xl"></div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <div className="inline-block mb-4">
            <Film className="w-12 h-12 text-burgundy mx-auto" strokeWidth={1.5} />
          </div>
          <h2 className="text-4xl md:text-5xl font-playfair font-bold text-burgundy mb-4">
            Comedy Clips
          </h2>
          <p className="text-xl text-gray-700 mb-6">
            Watch my latest performances in English and Italian
          </p>
          <div className="flex items-center justify-center gap-6">
            <a
              href={instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-burgundy hover:text-bordeaux transition-colors font-semibold text-lg hover:scale-105 transition-transform duration-300"
            >
              <Instagram className="w-6 h-6" />
              @funnybysurname_comedy
            </a>
            <a
              href="https://www.tiktok.com/@funnybbysurname"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-burgundy hover:text-bordeaux transition-colors font-semibold text-lg hover:scale-105 transition-transform duration-300"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
              @funnybbysurname
            </a>
          </div>
        </div>

        <Tabs defaultValue="english" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12 bg-pink/20 p-1 rounded-full shadow-lg border-2 border-burgundy/20">
            <TabsTrigger 
              value="english" 
              className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-burgundy data-[state=active]:to-bordeaux data-[state=active]:text-white transition-all duration-300 font-semibold"
            >
              English Comedy
            </TabsTrigger>
            <TabsTrigger 
              value="italian"
              className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-burgundy data-[state=active]:to-bordeaux data-[state=active]:text-white transition-all duration-300 font-semibold"
            >
              Italian Comedy
            </TabsTrigger>
          </TabsList>

          {videoCategories.map((category) => (
            <TabsContent key={category.id} value={category.id} className="mt-0">
              <p className="text-center text-gray-700 mb-8 text-lg font-medium">
                {category.description}
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                {category.videos.map((video) => (
                  <Card 
                    key={video.id} 
                    className="group cursor-pointer overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 border-burgundy/20 hover:border-burgundy/50 bg-white hover:-translate-y-2"
                  >
                    <div className="relative aspect-[9/16] overflow-hidden bg-gradient-to-br from-burgundy/10 via-pink/10 to-white">
                      <img 
                        src={video.thumbnail} 
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-burgundy/70 via-burgundy/30 to-transparent group-hover:from-burgundy/80 transition-all duration-300 flex items-center justify-center">
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-2xl">
                          <Play className="w-8 h-8 text-burgundy ml-1" fill="currentColor" />
                        </div>
                      </div>
                      <div className="absolute top-3 right-3 bg-white backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg border border-burgundy/20">
                        <Instagram className="w-4 h-4 text-burgundy" />
                        <span className="text-xs font-semibold text-gray-700">{video.platform}</span>
                      </div>
                    </div>
                    <CardContent className="p-4 bg-gradient-to-b from-white to-pink/5">
                      <h3 className="font-semibold text-gray-900 group-hover:text-burgundy transition-colors">
                        {video.title}
                      </h3>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Instagram embed placeholder */}
        <div className="mt-16 p-10 bg-gradient-to-br from-pink/30 via-white to-burgundy/5 rounded-3xl border-2 border-dashed border-burgundy/30 shadow-xl">
          <Instagram className="w-16 h-16 text-burgundy mx-auto mb-6" />
          <p className="text-center text-gray-700">
            <span className="block text-2xl font-playfair font-bold mb-3 text-burgundy">More clips on Instagram!</span>
            <span className="text-lg">Visit my profile for the latest comedy content and behind-the-scenes footage.</span>
          </p>
          <div className="text-center mt-6">
            <a
              href={instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-3 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 text-white rounded-full font-semibold hover:scale-105 transition-transform duration-300 shadow-lg"
            >
              Follow @funnybysurname_comedy
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Videos;
